import { useMemo } from 'react'
import {
  Alert,
  Box,
  Card,
  CardContent,
  Checkbox,
  InputLabel,
  TextField,
  Typography,
} from '@mui/material'
import AppsContainer from './apps-container'
import WizardFooter from '../WizardFooter'
import useOnboardingContext from '@/hooks/useOnboardingContext'
import { CheckCircle } from '@mui/icons-material'
import CalloutInfoIcon from '@/assets/callout_info.svg'
import { useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import isEmpty from 'lodash/isEmpty'
import AddressAutocomplete from './address-autocomplete'
import { InputMask } from "@react-input/mask";

const AppsSetup = ({
  handleExitToEditor,
  wizard: { title, subtitle } = {},
}) => {
  const { recommendedApps, appsSetupData, setAppsSetupData, isGrid, checkedSortedSections } = useOnboardingContext()
  const selectedApps = useMemo(() => {
    if (isGrid) {
      return recommendedApps.filter((app) => {
        const section = checkedSortedSections?.find(s => s.id === app.id);
        return section?.checked === true;
      })
    }
    return recommendedApps.filter((app) => app.enabled)
  }, [recommendedApps, checkedSortedSections, isGrid])
  // If isGrid, derive selected apps from checkedSortedSections (store/appointments/blog), otherwise use recommendedApps
  const { register, control, setValue } = useForm({
    defaultValues: appsSetupData
  })
  
  const [isFormSkippable, setIsFormSkippable] = useState(true)
  
  const fieldValues = useWatch({ control })

  useEffect(() => {
    if(!isEmpty(fieldValues)) {
      setAppsSetupData(fieldValues)
      const cannotSkip = Object.values(fieldValues).some(val => typeof val !== "boolean" && !isEmpty(val))
      setIsFormSkippable(!cannotSkip)
    }
  }, [fieldValues])

  useEffect(() => {
    register("businessPostalCode")
    register("shippingPostalCode")
  }, [])

  return (
    <AppsContainer
      title={title}
      subtitle={subtitle}
      handleExitToEditor={handleExitToEditor}
    >
      <Box className='mx-1 md:mx-0'>
        <Card
          sx={{
            border: '1px solid #C0C4CF',
            py: '12px',
            px: '12px',
            mb: '12px',
          }}
          elevation={0}
        >
          <CardContent>
            <Box className='flex gap-5 justify-center'>
              {selectedApps.map((app) => {
                const icons = import.meta.glob('/src/assets/*', { eager: true })
                const iconPath = icons[`/src/assets/${app.icon}`]?.default
                return (
                  <Box
                    key={app.name}
                    className='relative'
                  >
                    <img
                      className='w-[48px] h-[48px] rounded-xl'
                      style={{
                        background: app.background,
                        objectFit: 'none',
                      }}
                      src={iconPath}
                      alt='icon'
                    />
                    <CheckCircle
                      className='absolute -top-1 -right-2 bg-white rounded-full'
                      color='primary'
                      fontSize='small'
                      sx={{
                        width: 20,
                        height: 20,
                        border: '2px solid white',
                      }}
                    />
                  </Box>
                )
              })}
            </Box>
            <Box
              component={'form'}
              className='text-left mt-5 flex flex-col gap-5'
            >
              <div>
                <div className="flex flex-wrap gap-5 md:flex-nowrap">
                  <div className="w-full md:flex-[4]">
                    <InputLabel htmlFor="business-address">Business Address</InputLabel>
                    <AddressAutocomplete 
                      register={register}
                      name={"businessAddress"}
                      id={"business-address"}
                      validation={{ required: true }}
                      autoFocus={!fieldValues.businessAddress_input}
                      setValue={setValue}
                      value={fieldValues.businessAddress_input}
                      onPlaceSelected={(option) => {
                        setValue("businessPostalCode", option.zipcode, { shouldValidate: true })
                      }}
                    />
                  </div>
                  <div className="flex w-full md:w-auto gap-5 md:flex-[1]">
                    <div className="flex-1">
                      <InputLabel htmlFor="business-apt-suite">Apt/Suite</InputLabel>
                      <TextField
                        required
                        {...register("businessAptSuite")}
                        id="business-apt-suite"
                        variant="outlined"
                        slotProps={{ htmlInput: { maxLength: 50 } }}
                        fullWidth
                      />
                    </div>
                  </div>
                </div>
                <div className='mt-2 flex gap-2 items-center'>
                  <Checkbox
                    checked={fieldValues.sameShippingAddress ?? true}
                    {...register("sameShippingAddress")}
                    id='checkbox-shipping-address'
                    sx={{
                      borderRadius: 12,
                      padding: 0,
                    }}
                  />
                  <InputLabel
                    htmlFor='checkbox-shipping-address'
                    className='!m-0'
                  >
                    <Typography
                      variant='h6'
                      fontSize={12}
                    >
                      This is also my shipping address
                    </Typography>
                  </InputLabel>
                </div>
              </div>
              {!isEmpty(fieldValues) && !fieldValues.sameShippingAddress && (
                <div className="flex flex-wrap gap-5 md:flex-nowrap">
                  <div className="w-full md:flex-[4]">
                    <InputLabel htmlFor="shipping-address">Shipping Address</InputLabel>
                    <AddressAutocomplete 
                      register={register}
                      name={"shippingAddress"}
                      id={"shipping-address"}
                      validation={{
                        required: !fieldValues.sameShippingAddress
                      }}
                      setValue={setValue}
                      value={fieldValues.shippingAddress_input}
                      onPlaceSelected={(option) => {
                        setValue("shippingPostalCode", option.zipcode, { shouldValidate: true })
                      }}
                    />
                  </div>
                  <div className="flex w-full md:w-auto gap-5 md:flex-[1]">
                    <div className="flex-1">
                      <InputLabel htmlFor="shipping-apt-suite">Apt/Suite</InputLabel>
                      <TextField
                        required
                        {...register("shippingAptSuite")}
                        id="shipping-apt-suite"
                        variant="outlined"
                        slotProps={{ htmlInput: { maxLength: 50 } }}
                        fullWidth
                      />
                    </div>
                  </div>
                </div>
              )}
              <div>
                <InputLabel htmlFor='phone-number'>Phone Number</InputLabel>
                <TextField
                  required
                  {...register("phoneNumber")}
                  id="phone-number"
                  variant="outlined"
                  fullWidth
                  slotProps={{
                    input: {
                      inputComponent: InputMask,
                      inputProps: {
                        mask: "(___) ___-____",
                        replacement: { "_": /\d/ },
                        separate: false,
                        showMask: true,
                        onFocus: ({ target }) => {
                          const i = target.value?.indexOf("_") ?? 1
                          target.setSelectionRange(i, i)
                        },
                        onPointerDown: (e) => {
                          e.preventDefault()
                          e.target.focus()
                        }
                      },
                    }
                  }}
                />
              </div>
              <Alert severity='info' icon={<img src={CalloutInfoIcon} style={{
                width: 'auto',
                height: 30,
                minHeight: 30,
                alignSelf: 'flex-start',
                marginTop: -4,
              }}/>} 
              sx={{
                backgroundColor: '#0938441A',
                padding: "0px 8px"
              }}>
                <Typography variant='subtitle2' fontSize={12} color='textPrimary'>
                  We respect your privacy. Your address and phone number will only be used for shipping and tax purposes and will not be used for marketing purposes or shared with any third parties.
                </Typography>
              </Alert>
            </Box>
          </CardContent>
        </Card>
        <Box className="fixed bottom-0 left-0 right-5 md:static">
          <WizardFooter skip={isFormSkippable}/>
        </Box>
      </Box>
    </AppsContainer>
  )
}

export default AppsSetup
